import React, { useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, Card, CardContent, CircularProgress, TextField, Typography } from "@mui/material";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { FirebaseContext } from "common";
import { useTranslation } from "react-i18next";
import { MAIN_COLOR, SECONDORY_COLOR } from "../common/sharedFunctions";

const getLabel = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const checkPasswordValidity = (password, confirmPassword, t) => {
  if (password !== confirmPassword) {
    return getLabel(t, "confirm_password_not_match_err", "Confirm password does not match");
  }
  if (!/^\S*$/.test(password)) {
    return getLabel(t, "password_must_not_contain_whitespaces", "Password must not contain whitespaces.");
  }
  if (!/^(?=.*[A-Z]).*$/.test(password)) {
    return getLabel(t, "password_must_have_at_least_one_uppercase_character", "Password must have at least one uppercase character.");
  }
  if (!/^(?=.*[a-z]).*$/.test(password)) {
    return getLabel(t, "password_must_have_at_least_one_lowercase_character", "Password must have at least one lowercase character.");
  }
  if (!/^(?=.*[0-9]).*$/.test(password)) {
    return getLabel(t, "password_must_contain_at_least_one_digit", "Password must contain at least one digit.");
  }
  if (!/^.{8,16}$/.test(password)) {
    return getLabel(t, "password_must_be_8-16_characters_long", "Password must be 8-16 characters long.");
  }
  return null;
};

const ResetPassword = () => {
  const { t } = useTranslation();
  const firebase = useContext(FirebaseContext);
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const mode = searchParams.get("mode");
  const oobCode = searchParams.get("oobCode");

  useEffect(() => {
    if (mode !== "resetPassword" || !oobCode) {
      setError(getLabel(t, "invalid_password_reset_link", "Invalid password reset link."));
      setLoading(false);
      return;
    }

    verifyPasswordResetCode(firebase.auth, oobCode)
      .then((accountEmail) => {
        setEmail(accountEmail);
        setLoading(false);
      })
      .catch(() => {
        setError(getLabel(t, "password_reset_link_expired", "This password reset link has expired or was already used. Please request a new reset email."));
        setLoading(false);
      });
  }, [firebase.auth, mode, oobCode, t]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationError = checkPasswordValidity(password, confirmPassword, t);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");
    confirmPasswordReset(firebase.auth, oobCode, password)
      .then(() => {
        setSuccess(true);
        setSaving(false);
      })
      .catch(() => {
        setError(getLabel(t, "password_reset_failed", "Password reset failed. Please request a new reset email and try again."));
        setSaving(false);
      });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backgroundColor: SECONDORY_COLOR,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 460, borderRadius: "8px" }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: "bold", color: MAIN_COLOR }}>
            {getLabel(t, "reset_password_title", "Reset Password")}
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {email || getLabel(t, "reset_password_instruction", "Enter your new password.")}
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : success ? (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                {getLabel(t, "password_reset_success", "Your password was reset successfully. You can now login.")}
              </Alert>
              <Button component={Link} to="/login" variant="contained" fullWidth>
                {getLabel(t, "login", "Login")}
              </Button>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              ) : null}
              {!error || email ? (
                <>
                  <TextField
                    type="password"
                    label={getLabel(t, "password", "Password")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <TextField
                    type="password"
                    label={getLabel(t, "confirm_password", "Confirm Password")}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    fullWidth
                    margin="normal"
                  />
                  <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }} disabled={saving}>
                    {saving ? <CircularProgress size={22} /> : getLabel(t, "save_new_password", "Save New Password")}
                  </Button>
                </>
              ) : null}
              <Button component={Link} to="/login" fullWidth sx={{ mt: 2 }}>
                {getLabel(t, "back_to_login", "Back to Login")}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPassword;
